import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { taskDocumentKeys } from "@/lib/query";

export function useProjectTaskDocumentCount(projectId: string) {
  return useQuery({
    queryKey: taskDocumentKeys.count(projectId),
    queryFn: () => apiFetch<{ count: number }>(`/projects/${projectId}/task-documents/count`),
    enabled: !!projectId,
  });
}

export type FileTypeBucket = "pdf" | "image" | "office" | "other";
export type TaskDocumentSortField = "createdAt" | "fileName" | "taskTitle" | "uploaderName";
export type SortDirection = "asc" | "desc";
export type TaskStatus =
  | "not_started"
  | "in_progress"
  | "completed"
  | "blocked"
  | "skipped"
  | "overdue";

export interface TaskDocumentFilters {
  search?: string;
  taskStatus?: TaskStatus[];
  milestoneId?: string[];
  uploaderUserId?: string[];
  uploaderCompanyId?: string[];
  fileType?: FileTypeBucket[];
  from?: string;
  to?: string;
  sortBy?: TaskDocumentSortField;
  sortDir?: SortDirection;
  limit?: number;
}

export interface TaskDocumentRow {
  fileId: string;
  fileName: string;
  mimeType: string | null;
  fileSizeBytes: number | null;
  createdAt: string;
  taskId: string;
  taskTitle: string | null;
  taskStatus: string | null;
  milestoneId: string | null;
  milestoneName: string | null;
  uploaderUserId: string | null;
  uploaderName: string | null;
  uploaderCompanyId: string | null;
  uploaderCompanyName: string | null;
}

interface TaskDocumentsPage {
  data: TaskDocumentRow[];
  nextCursor: { createdAt: string; id: string } | null;
  hasNext: boolean;
}

export interface TaskDocumentFacets {
  uploaders: Array<{ id: string; name: string }>;
  uploaderCompanies: Array<{ id: string; name: string }>;
  milestones: Array<{ id: string; name: string }>;
}

function buildQueryString(
  filters: TaskDocumentFilters,
  cursor: { createdAt: string; id: string } | null,
): string {
  const sp = new URLSearchParams();
  if (filters.search) sp.set("search", filters.search);
  for (const s of filters.taskStatus ?? []) sp.append("taskStatus", s);
  for (const m of filters.milestoneId ?? []) sp.append("milestoneId", m);
  for (const u of filters.uploaderUserId ?? []) sp.append("uploaderUserId", u);
  for (const c of filters.uploaderCompanyId ?? []) sp.append("uploaderCompanyId", c);
  for (const f of filters.fileType ?? []) sp.append("fileType", f);
  if (filters.from) sp.set("from", filters.from);
  if (filters.to) sp.set("to", filters.to);
  if (filters.sortBy) sp.set("sortBy", filters.sortBy);
  if (filters.sortDir) sp.set("sortDir", filters.sortDir);
  if (filters.limit) sp.set("limit", String(filters.limit));
  if (cursor) {
    sp.set("cursorCreatedAt", cursor.createdAt);
    sp.set("cursorId", cursor.id);
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
}

export function useProjectTaskDocuments(projectId: string, filters: TaskDocumentFilters) {
  return useInfiniteQuery({
    queryKey: taskDocumentKeys.project(projectId, filters as Record<string, unknown>),
    enabled: !!projectId,
    initialPageParam: null as { createdAt: string; id: string } | null,
    queryFn: ({ pageParam }) =>
      apiFetch<TaskDocumentsPage>(
        `/projects/${projectId}/task-documents${buildQueryString(filters, pageParam)}`,
      ),
    getNextPageParam: (lastPage) => (lastPage.hasNext ? lastPage.nextCursor : undefined),
  });
}

export function useProjectTaskDocumentFacets(projectId: string) {
  return useQuery({
    queryKey: ["task-documents", projectId, "facets"] as const,
    enabled: !!projectId,
    queryFn: () => apiFetch<TaskDocumentFacets>(`/projects/${projectId}/task-documents/facets`),
  });
}
